import { memo, useMemo } from 'react';

import { type V3ChatActions } from '@apps/web/features/phoebe_chat_v3/model/actions';
import { type V3PresentationContext } from '@apps/web/features/phoebe_chat_v3/model/presentation_context';
import {
    useV3Entry,
    useV3EntryOrder,
    type V3RunStore,
} from '@apps/web/features/phoebe_chat_v3/model/run_store';
import { V3CommsApprovalGroupUi } from '@apps/web/features/phoebe_chat_v3/ui/dynamic_ui';
import { V3MessageRow } from '@apps/web/features/phoebe_chat_v3/ui/message_row';
import { V3ReasoningRow } from '@apps/web/features/phoebe_chat_v3/ui/reasoning_row';
import { V3StatusRow } from '@apps/web/features/phoebe_chat_v3/ui/status_row';
import { V3ToolRow } from '@apps/web/features/phoebe_chat_v3/ui/tool_row';

const V3EntryRow = memo(function V3EntryRow({
    id,
    transcriptIndex,
    store,
    presentationContext,
    actions,
}: {
    readonly id: string;
    readonly transcriptIndex: number;
    readonly store: V3RunStore;
    readonly presentationContext: V3PresentationContext | null;
    readonly actions: V3ChatActions;
}) {
    const entry = useV3Entry(store, id);
    if (entry === null) {
        return null;
    }
    switch (entry.kind) {
        case 'message':
            return (
                <V3MessageRow
                    entry={entry}
                    presentationContext={presentationContext}
                />
            );
        case 'reasoning':
            return <V3ReasoningRow entry={entry} />;
        case 'tool':
            return (
                <V3ToolRow
                    entry={entry}
                    transcriptIndex={transcriptIndex}
                    presentationContext={presentationContext}
                    actions={actions}
                />
            );
        case 'comms-approval-group':
            return <V3CommsApprovalGroupUi group={entry} actions={actions} />;
        case 'status':
            return <V3StatusRow entry={entry} actions={actions} />;
        default: {
            const exhaustiveEntry: never = entry;
            return exhaustiveEntry;
        }
    }
});

export const V3Transcript = memo(function V3Transcript({
    store,
    presentationContext,
    actions,
    hideThisTurnWork = false,
}: {
    readonly store: V3RunStore;
    readonly presentationContext: V3PresentationContext | null;
    readonly actions: V3ChatActions;
    readonly hideThisTurnWork?: boolean;
}) {
    const order = useV3EntryOrder(store);
    const { hiddenReasoningIds, promptActionEntryIds } = useMemo(() => {
        const currentTurnEntries = [];
        let currentPromptActionTurnId: string | null = null;
        let foundPromptActionTurn = false;
        for (let index = order.length - 1; index >= 0; index -= 1) {
            const id = order[index];
            if (id === undefined) {
                continue;
            }
            const entry = store.getEntrySnapshot(id);
            if (entry?.kind === 'message' && entry.role === 'user') {
                break;
            }
            currentTurnEntries.push({ id, entry });
            if (
                !foundPromptActionTurn &&
                entry?.kind === 'tool' &&
                entry.dataPresentation !== null
            ) {
                currentPromptActionTurnId = entry.agentTurnId;
                foundPromptActionTurn = true;
            }
        }
        const hidden = new Set<string>();
        const enabled = new Set<string>();
        for (const { id, entry } of currentTurnEntries) {
            // The live trace includes this turn's work. Hide the separate
            // Worked disclosure while the trace is visible so the two never
            // compete.
            if (hideThisTurnWork && entry?.kind === 'reasoning') {
                hidden.add(id);
            }
            if (
                currentPromptActionTurnId === null ||
                (entry?.kind === 'tool' &&
                    entry.agentTurnId === currentPromptActionTurnId)
            ) {
                enabled.add(id);
            }
        }
        return {
            hiddenReasoningIds: hidden,
            promptActionEntryIds: enabled,
        };
    }, [hideThisTurnWork, order, store]);
    const historicalActions = useMemo<V3ChatActions>(
        () => ({ ...actions, onPromptSubmit: undefined }),
        [actions],
    );
    return (
        <div className="mx-auto flex w-full max-w-[792px] flex-col gap-3.5 px-4 py-6">
            {order.map((id, transcriptIndex) => {
                if (hiddenReasoningIds.has(id)) {
                    return null;
                }
                return (
                    <V3EntryRow
                        key={id}
                        id={id}
                        transcriptIndex={transcriptIndex}
                        store={store}
                        presentationContext={presentationContext}
                        actions={
                            promptActionEntryIds.has(id)
                                ? actions
                                : historicalActions
                        }
                    />
                );
            })}
        </div>
    );
});
